// ============================================================================
// @torre/expenses — ¿Cuándo se gastó? (#955, épica #959)
// ============================================================================
// Ritmo de salida de dinero: serie diaria / semanal / mensual, mapa de calor
// por día, principales comercios y comparación contra el periodo anterior.
//
// Regla de moneda: todo suma en pesos (`amountBase`). Un gasto en otra moneda
// SIN tipo de cambio no se suma como si fueran pesos: va aparte, por moneda.
//
// Sin I/O. Nada importado de `./index` en el nivel superior (ver accounts.ts).
// ============================================================================

import { round2, vendorTokens, type Expense } from "./index";

export const TIMELINE_GRANULARITIES = ["dia", "semana", "mes"] as const;
export type TimelineGranularity = (typeof TIMELINE_GRANULARITIES)[number];

/** Lo mínimo de un gasto que usa la línea de tiempo. */
export type TimelineExpense = Pick<Expense, "id" | "date" | "amount" | "currency" | "amountBase" | "vendor" | "category"> &
  Partial<Pick<Expense, "platformId" | "description" | "source">> & { accountId?: string | null };

export interface TimelineFilters {
  accountId?: string | null;
  category?: string | null;
  platformId?: string | null;
}

export function filterTimeline<T extends TimelineExpense>(rows: readonly T[], f: TimelineFilters = {}): T[] {
  return rows.filter(
    (e) =>
      (!f.accountId || e.accountId === f.accountId) &&
      (!f.category || e.category === f.category) &&
      (!f.platformId || e.platformId === f.platformId),
  );
}

export interface TimelineBucket {
  /** Día (YYYY-MM-DD), lunes de la semana (YYYY-MM-DD) o mes (YYYY-MM). */
  key: string;
  /** Pesos. */
  total: number;
  count: number;
  /** Montos sin tipo de cambio, por moneda: NO están en `total`. */
  foreign: Record<string, number>;
}

const DAY_MS = 86_400_000;
const toDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const msOf = (iso: string) => Date.parse(`${iso}T00:00:00Z`);

/** Lunes (YYYY-MM-DD) de la semana de una fecha. */
export function weekStart(date: string): string {
  const ms = msOf(date);
  const dow = (new Date(ms).getUTCDay() + 6) % 7; // 0 = lunes
  return toDay(ms - dow * DAY_MS);
}

export function bucketKey(date: string, g: TimelineGranularity): string {
  if (g === "mes") return date.slice(0, 7);
  if (g === "semana") return weekStart(date);
  return date;
}

/** Todas las claves entre dos fechas, para que los huecos salgan en cero. */
export function bucketKeys(from: string, to: string, g: TimelineGranularity): string[] {
  const out: string[] = [];
  if (!from || !to || from > to) return out;
  if (g === "mes") {
    let [y, m] = from.slice(0, 7).split("-").map(Number);
    const end = to.slice(0, 7);
    for (let k = `${y}-${String(m).padStart(2, "0")}`; k <= end && out.length < 600; ) {
      out.push(k);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      k = `${y}-${String(m).padStart(2, "0")}`;
    }
    return out;
  }
  const step = g === "semana" ? 7 : 1;
  for (let ms = msOf(g === "semana" ? weekStart(from) : from); ms <= msOf(to) && out.length < 1200; ms += step * DAY_MS) out.push(toDay(ms));
  return out;
}

const addForeign = (b: { foreign: Record<string, number> }, e: TimelineExpense) => {
  const cur = (e.currency || "XXX").toUpperCase();
  b.foreign[cur] = round2((b.foreign[cur] || 0) + Number(e.amount || 0));
};

/** Serie de gasto en el tiempo, con los huecos en cero. */
export function spendingSeries(rows: readonly TimelineExpense[], from: string, to: string, g: TimelineGranularity): TimelineBucket[] {
  const map = new Map<string, TimelineBucket>(bucketKeys(from, to, g).map((k) => [k, { key: k, total: 0, count: 0, foreign: {} }]));
  for (const e of rows) {
    if (!e.date || e.date < from || e.date > to) continue;
    const key = bucketKey(e.date, g);
    let b = map.get(key);
    if (!b) {
      b = { key, total: 0, count: 0, foreign: {} };
      map.set(key, b);
    }
    b.count += 1;
    if (e.amountBase == null) addForeign(b, e);
    else b.total = round2(b.total + e.amountBase);
  }
  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

/** Total por día (para el calendario / mapa de calor). */
export function dailyTotals(rows: readonly TimelineExpense[], from: string, to: string): Record<string, TimelineBucket> {
  return Object.fromEntries(spendingSeries(rows, from, to, "dia").map((b) => [b.key, b]));
}

/**
 * Intensidad 0..4 de cada día para el mapa de calor, por cuartiles de los
 * días con gasto (así un solo cargo enorme no deja todo lo demás en blanco).
 */
export function heatLevels(days: Record<string, TimelineBucket>): Record<string, 0 | 1 | 2 | 3 | 4> {
  const values = Object.values(days).map((d) => d.total).filter((v) => v > 0);
  const n = values.length;
  const out: Record<string, 0 | 1 | 2 | 3 | 4> = {};
  for (const [k, d] of Object.entries(days)) {
    if (d.total <= 0) {
      out[k] = 0;
      continue;
    }
    // Posición del día entre los días con gasto (empates = misma posición).
    const rank = values.filter((v) => v <= d.total).length;
    out[k] = Math.min(4, Math.max(1, Math.ceil((4 * rank) / n))) as 1 | 2 | 3 | 4;
  }
  return out;
}

export interface VendorTotal {
  /** Clave normalizada con que se agrupa ("amazon web services"). */
  key: string;
  /** Nombre más frecuente con que aparece. */
  vendor: string;
  total: number;
  count: number;
  foreign: Record<string, number>;
}

const vendorKey = (v: string) => vendorTokens(v).join(" ") || String(v || "").trim().toLowerCase() || "sin comercio";

/** Comercios ordenados por lo gastado (en pesos). */
export function vendorTotals(rows: readonly TimelineExpense[]): VendorTotal[] {
  const map = new Map<string, VendorTotal & { names: Map<string, number> }>();
  for (const e of rows) {
    const key = vendorKey(e.vendor);
    let v = map.get(key);
    if (!v) {
      v = { key, vendor: e.vendor, total: 0, count: 0, foreign: {}, names: new Map() };
      map.set(key, v);
    }
    v.count += 1;
    v.names.set(e.vendor, (v.names.get(e.vendor) || 0) + 1);
    if (e.amountBase == null) addForeign(v, e);
    else v.total = round2(v.total + e.amountBase);
  }
  return [...map.values()]
    .map(({ names, ...v }) => ({ ...v, vendor: [...names.entries()].sort((a, b) => b[1] - a[1])[0][0] }))
    .sort((a, b) => b.total - a.total || b.count - a.count);
}

/** Rango inmediatamente anterior con el mismo número de días. */
export function previousRange(from: string, to: string): { from: string; to: string } {
  const days = Math.round((msOf(to) - msOf(from)) / DAY_MS) + 1;
  const prevTo = msOf(from) - DAY_MS;
  return { from: toDay(prevTo - (days - 1) * DAY_MS), to: toDay(prevTo) };
}

export interface PeriodComparison {
  current: { from: string; to: string; total: number; count: number; foreign: Record<string, number> };
  previous: { from: string; to: string; total: number; count: number; foreign: Record<string, number> };
  delta: number;
  /** `null` si el periodo anterior fue 0 (no hay porcentaje honesto). */
  deltaPct: number | null;
  /** Top comercios del periodo con lo que gastaron en el anterior. */
  topVendors: (VendorTotal & { previous: number; delta: number })[];
}

/**
 * Compara `from`–`to` contra el periodo anterior de la misma duración.
 * `rows` debe traer los gastos de ambos periodos.
 */
export function comparePeriods(rows: readonly TimelineExpense[], from: string, to: string, limit = 10): PeriodComparison {
  const prev = previousRange(from, to);
  const cur = rows.filter((e) => e.date >= from && e.date <= to);
  const old = rows.filter((e) => e.date >= prev.from && e.date <= prev.to);
  const sum = (list: readonly TimelineExpense[]) => {
    const acc = { total: 0, count: list.length, foreign: {} as Record<string, number> };
    for (const e of list) {
      if (e.amountBase == null) addForeign(acc, e);
      else acc.total = round2(acc.total + e.amountBase);
    }
    return acc;
  };
  const a = sum(cur);
  const b = sum(old);
  const oldByVendor = new Map(vendorTotals(old).map((v) => [v.key, v.total]));
  const topVendors = vendorTotals(cur)
    .slice(0, limit)
    .map((v) => {
      const previous = oldByVendor.get(v.key) || 0;
      return { ...v, previous, delta: round2(v.total - previous) };
    });
  return {
    current: { from, to, ...a },
    previous: { ...prev, ...b },
    delta: round2(a.total - b.total),
    deltaPct: b.total > 0 ? Math.round(((a.total - b.total) / b.total) * 1000) / 10 : null,
    topVendors,
  };
}
